import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Modal, Input, Space, Typography, Select,
  message, Popconfirm, Badge, Divider, Spin, Tooltip, Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  MessageOutlined, SendOutlined, ReloadOutlined, SearchOutlined,
  CheckCircleOutlined, ClockCircleOutlined, StopOutlined, DeleteOutlined,
  PaperClipOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { inquiryAPI } from '../services/api';
import { gray, brand } from '../theme/tokens';
import { usePermission } from '../contexts/PermissionContext';
import { ATTACH_MAX_BYTES, filesToUploadItems } from '../components/InquiryPanel';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { fmtDateTime } from '../utils/date';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface Attachment {
  id: number;
  file_name: string;
  file_size: number;
  content_type: string;
  uploader_type: string;
  uploader_name: string;
  created_at?: string;
}

interface Reply {
  id: number;
  author_type: 'admin' | 'partner';
  author_name: string;
  content: string;
  created_at: string;
  attachments?: Attachment[];
}

interface Inquiry {
  id: number;
  schedule_id?: number;
  test_number: string;
  schedule_title: string;
  schedule_status: string;
  company_id: number;
  company_name: string;
  company_code: string;
  partner_username: string;
  inquiry_type: string;
  title: string;
  content: string;
  status: string;
  admin_unread: boolean;
  reply_count: number;
  last_reply_at?: string;
  created_at: string;
  attachment_count?: number;
  attachments?: Attachment[];
  replies?: Reply[];
}

const STATUS_META: Record<string, { color: string; icon: React.ReactNode }> = {
  '대기':     { color: 'orange', icon: <ClockCircleOutlined /> },
  '답변완료': { color: 'green',  icon: <CheckCircleOutlined /> },
  '수용':     { color: 'blue',   icon: <CheckCircleOutlined /> },
  '반려':     { color: 'red',    icon: <StopOutlined /> },
  '종료':     { color: 'default', icon: <StopOutlined /> },
};

/* 구분별 허용 상태 — 백엔드 INQUIRY_STATUSES_BY_TYPE 와 동일 기준.
   요청은 '답변완료'가 중간 상태이고 수용(반영완료)/반려로 닫혀야 조치 결과가 남는다. */
const STATUS_BY_TYPE: Record<string, string[]> = {
  '문의': ['대기', '답변완료', '종료'],
  '요청': ['대기', '답변완료', '수용', '반려', '종료'],
};
const ALL_STATUSES = ['대기', '답변완료', '수용', '반려', '종료'];

// 화면 카운터 = 백엔드 검증 기준 (main.py INQUIRY_CONTENT_MAX)
const CONTENT_MAX = 2000;

const formatBytes = (n = 0) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

const fmt = (v?: string) => fmtDateTime(v);

const InquiryPage: React.FC = () => {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('inquiries', 'WRITE');
  const canDelete = hasPermission('inquiries', 'DELETE');
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();

  const [rows, setRows] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [keyword, setKeyword] = useState('');

  const [detail, setDetail] = useState<Inquiry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<UploadFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  const downloadAttachment = async (a: Attachment) => {
    setDownloading(a.id);
    try {
      const res = await inquiryAPI.downloadFile(a.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = a.file_name;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('다운로드에 실패했습니다.');
    } finally {
      setDownloading(null);
    }
  };

  /** 첨부 칩 목록 — 문의 본문/답글 공용 */
  const renderAttachments = (items?: Attachment[]) => {
    if (!items || items.length === 0) return null;
    return (
      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((a) => (
          <Space key={a.id} size={4}
            style={{ background: '#fff', border: `1px solid ${gray[200]}`, borderRadius: 8, padding: '3px 8px', fontSize: 12 }}>
            <PaperClipOutlined style={{ color: gray[400], fontSize: 12 }} />
            <Tooltip title={`${a.file_name} · ${formatBytes(a.file_size)} · ${a.uploader_name}`}>
              <span onClick={() => downloadAttachment(a)}
                style={{ cursor: 'pointer', color: brand.primary, maxWidth: 240, overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}>
                {a.file_name}
              </span>
            </Tooltip>
            <span style={{ color: gray[300], fontSize: 11 }}>{formatBytes(a.file_size)}</span>
            <Button type="text" size="small" loading={downloading === a.id}
              icon={<DownloadOutlined style={{ fontSize: 12 }} />} onClick={() => downloadAttachment(a)} />
          </Space>
        ))}
      </div>
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inquiryAPI.list({
        status: statusFilter,
        inquiry_type: typeFilter,
        keyword: keyword.trim() || undefined,
      });
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, keyword]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: number) => {
    setDetail({ id } as Inquiry);
    setDetailLoading(true);
    setReplyText('');
    setReplyFiles([]);
    try {
      const res = await inquiryAPI.detail(id);
      setDetail(res.data);
      setRows(prev => prev.map(r => (r.id === id ? { ...r, admin_unread: false } : r)));
    } catch {
      message.error('문의를 불러오지 못했습니다.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReply = async () => {
    if (!detail || !replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await inquiryAPI.reply(detail.id, replyText.trim());
      // 방금 등록한 답변에 첨부파일 연결
      const newReplyId = (res.data.replies || []).slice(-1)[0]?.id;
      const failed: string[] = [];
      for (const f of replyFiles) {
        try {
          await inquiryAPI.uploadFile(detail.id, (f.originFileObj || f) as File, newReplyId);
        } catch {
          failed.push(f.name);
        }
      }
      if (failed.length) message.warning(`답변은 등록되었으나 첨부 실패: ${failed.join(', ')}`);
      else message.success('답변이 등록되었습니다. 파트너사 로그인 첫 화면에 알림이 표시됩니다.');
      setReplyText('');
      setReplyFiles([]);
      if (failed.length !== replyFiles.length && replyFiles.length) {
        const fresh = await inquiryAPI.detail(detail.id);
        setDetail(fresh.data);
      } else {
        setDetail(res.data);
      }
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '답변 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (id: number, status: string) => {
    try {
      const res = await inquiryAPI.updateStatus(id, status);
      if (detail?.id === id) setDetail(res.data);
      message.success(`상태를 '${status}'(으)로 변경했습니다.`);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '변경에 실패했습니다.');
    }
  };

  const removeInquiry = async (id: number) => {
    try {
      await inquiryAPI.delete(id);
      message.success('삭제되었습니다.');
      if (detail?.id === id) setDetail(null);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const waitingCount = rows.filter(r => r.status === '대기').length;

  const columns = [
    {
      title: '구분', dataIndex: 'inquiry_type', width: 72, align: 'center' as const,
      render: (v: string) => <Tag color={v === '요청' ? 'purple' : 'blue'} style={{ margin: 0, fontWeight: 600 }}>{v}</Tag>,
    },
    {
      title: '제목', dataIndex: 'title', ellipsis: true,
      render: (v: string, r: Inquiry) => (
        <Space size={6}>
          {r.admin_unread && <Badge dot color="#ef4444" />}
          <span style={{ fontWeight: r.admin_unread ? 700 : 500, color: gray[700] }}>{v}</span>
          {r.reply_count > 0 && <Tag style={{ fontSize: 10, margin: 0 }}>답글 {r.reply_count}</Tag>}
          {!!r.attachment_count && (
            <Tag icon={<PaperClipOutlined />} style={{ fontSize: 10, margin: 0 }}>{r.attachment_count}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '의뢰사', dataIndex: 'company_name', width: 130, responsive: ['md'] as any,
      render: (v: string, r: Inquiry) => (
        <Space size={4}>
          {r.company_code && <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>{r.company_code}</Tag>}
          <span style={{ fontSize: 12 }}>{v}</span>
        </Space>
      ),
    },
    {
      title: '시험번호', dataIndex: 'test_number', width: 130, responsive: ['md'] as any, 
      render: (v: string, r: Inquiry) => v
        ? <Tooltip title={r.schedule_title}><span style={{ fontWeight: 700, color: brand.primary, fontSize: 12 }}>{v}</span></Tooltip>
        : <Text type="secondary" style={{ fontSize: 12 }}>일반문의</Text>,
    },
    { title: '작성자', dataIndex: 'partner_username', width: 100, align: 'center' as const, responsive: ['md'] as any },
    {
      title: '등록일시', dataIndex: 'created_at', width: 140, align: 'center' as const, responsive: ['md'] as any, 
      render: (v: string) => <span style={{ fontSize: 12, color: gray[500] }}>{fmt(v)}</span>,
    },
    {
      title: '상태', dataIndex: 'status', width: 100, align: 'center' as const,
      render: (v: string) => {
        const m = STATUS_META[v] || STATUS_META['대기'];
        return <Tag color={m.color} icon={m.icon} style={{ margin: 0 }}>{v}</Tag>;
      },
    },
    {
      title: '관리', key: 'action', width: 84, align: 'center' as const, responsive: ['md'] as any, 
      render: (_: any, r: Inquiry) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}>
            {canWrite ? '답변' : '보기'}
          </Button>
          {canDelete && (
            <Popconfirm title="이 문의를 삭제할까요?" onConfirm={(e) => { (e as any)?.stopPropagation?.(); removeInquiry(r.id); }} okText="삭제" cancelText="취소">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div ref={cardWrapRef}>
      <Card
        size="small"
        title={
          <Space>
            <MessageOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700 }}>파트너 문의 / 요청</span>
            {waitingCount > 0 && <Tag color="orange">답변대기 {waitingCount}건</Tag>}
          </Space>
        }
        extra={
          <Space size={8}>
            <Select
              allowClear placeholder="구분" style={{ width: 100 }} size="small"
              value={typeFilter} onChange={setTypeFilter}
              options={['문의', '요청'].map(v => ({ value: v, label: v }))}
            />
            <Select
              allowClear placeholder="상태" style={{ width: 130 }} size="small"
              value={statusFilter} onChange={setStatusFilter}
              options={(typeFilter ? STATUS_BY_TYPE[typeFilter] : ALL_STATUSES).map(v => ({ value: v, label: v }))}
            />
            <Input
              size="small" style={{ width: 200 }} allowClear
              placeholder="제목/내용/작성자 검색"
              prefix={<SearchOutlined style={{ color: gray[400] }} />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={load}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={load}>새로고침</Button>
          </Space>
        }
      >
        <div ref={tableWrapRef}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={rows}
          columns={columns}
          onRow={(r) => ({ onClick: () => openDetail(r.id), style: { cursor: 'pointer' } })}
          rowClassName={(r) => (r.admin_unread ? 'inquiry-unread-row' : '')}
          scroll={{ y: bodyHeight }}
          pagination={{
            defaultPageSize: 20, showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
            size: 'small',
          }}
          locale={{ emptyText: '접수된 문의/요청이 없습니다.' }}
        />
        </div>
      </Card>

      {/* 상세 / 답변 모달 */}
      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        width={720}
        title={
          <Space>
            <MessageOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700 }}>{detail?.title || '문의 상세'}</span>
            {detail?.status && (
              <Tag color={(STATUS_META[detail.status] || STATUS_META['대기']).color}>{detail.status}</Tag>
            )}
          </Space>
        }
        footer={
          <Space>
            {/* 요청은 답변만으로 끝나지 않는다 — 조치 결과(수용/반려)로 닫아야 이력이 남는다 */}
            {canWrite && detail?.inquiry_type === '요청' && detail?.status !== '종료' && (
              <>
                <Button
                  type="primary" ghost disabled={detail?.status === '수용'}
                  onClick={() => detail && changeStatus(detail.id, '수용')}
                >수용 (반영완료)</Button>
                <Button
                  danger disabled={detail?.status === '반려'}
                  onClick={() => detail && changeStatus(detail.id, '반려')}
                >반려</Button>
              </>
            )}
            {canWrite && detail?.status !== '종료' && (
              <Button onClick={() => detail && changeStatus(detail.id, '종료')}>
                {detail?.inquiry_type === '요청' ? '종료' : '문의 종료'}
              </Button>
            )}
            {canWrite && detail?.status === '종료' && (
              <Button onClick={() => detail && changeStatus(detail.id, '답변완료')}>종료 취소</Button>
            )}
            <Button type="primary" onClick={() => setDetail(null)}>닫기</Button>
          </Space>
        }
      >
        {detailLoading || !detail?.created_at ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: gray[500], marginBottom: 12 }}>
              <Space size={10} wrap>
                {detail.company_code && <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>{detail.company_code}</Tag>}
                <span>{detail.company_name}</span>
                <span>작성자 <b style={{ color: gray[700] }}>{detail.partner_username}</b></span>
                {detail.test_number && (
                  <span>시험번호 <b style={{ color: brand.primary }}>{detail.test_number}</b>
                    {detail.schedule_title ? ` · ${detail.schedule_title}` : ''}</span>
                )}
                <span>{fmt(detail.created_at)}</span>
              </Space>
            </div>

            {/* 최초 문의 */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: gray[500], marginBottom: 6 }}>
                <Tag color={detail.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ marginRight: 6 }}>{detail.inquiry_type}</Tag>
                파트너사 문의
              </div>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{detail.content}</Paragraph>
              {renderAttachments(detail.attachments)}
            </div>

            {/* 스레드 */}
            {(detail.replies || []).map((r) => {
              const isAdmin = r.author_type === 'admin';
              return (
                <div
                  key={r.id}
                  style={{
                    background: isAdmin ? '#eef2ff' : '#f8fafc',
                    border: `1px solid ${isAdmin ? '#c7d2fe' : '#e2e8f0'}`,
                    borderRadius: 10, padding: 12, marginBottom: 10,
                    marginLeft: isAdmin ? 32 : 0, marginRight: isAdmin ? 0 : 32,
                  }}
                >
                  <div style={{ fontSize: 11, color: isAdmin ? brand.primary : gray[500], marginBottom: 6, fontWeight: 600 }}>
                    {isAdmin ? `담당자 (${r.author_name})` : `파트너 (${r.author_name})`}
                    <span style={{ fontWeight: 400, marginLeft: 8, color: gray[400] }}>{fmt(r.created_at)}</span>
                  </div>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{r.content}</Paragraph>
                  {renderAttachments(r.attachments)}
                </div>
              );
            })}

            {canWrite && detail.status !== '종료' && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                {/* 작성 영역 어디에나 파일을 끌어다 놓으면 첨부된다 */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault(); setDragOver(false);
                    const dropped = Array.from(e.dataTransfer?.files || []);
                    if (dropped.length) setReplyFiles((prev) => [...prev, ...filesToUploadItems(dropped)]);
                  }}
                  style={{
                    border: `2px dashed ${dragOver ? brand.primary : 'transparent'}`,
                    background: dragOver ? '#eef2ff' : 'transparent',
                    borderRadius: 8, padding: 6, transition: 'all .15s',
                  }}
                >
                  <TextArea
                    rows={4} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                    placeholder="파트너사에 전달할 답변을 입력하세요. 등록 시 파트너 포털 첫 화면에 알림이 표시됩니다. (파일을 이곳에 끌어다 놓으면 첨부됩니다)"
                    maxLength={CONTENT_MAX} showCount
                  />
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                    <Upload
                      multiple
                      fileList={replyFiles}
                      beforeUpload={(file) => {
                        if (file.size > ATTACH_MAX_BYTES) {
                          message.error(`${file.name}: 20MB를 초과할 수 없습니다.`);
                          return Upload.LIST_IGNORE;
                        }
                        return false;   // 답변 등록 후 일괄 전송
                      }}
                      onChange={({ fileList: fl }) => setReplyFiles(fl)}
                      onRemove={(file) => setReplyFiles((prev) => prev.filter((f) => f.uid !== file.uid))}
                    >
                      <Button size="small" icon={<PaperClipOutlined />}>파일 첨부</Button>
                    </Upload>
                    <div style={{ marginLeft: 'auto' }}>
                      <Button
                        type="primary" icon={<SendOutlined />} loading={submitting}
                        disabled={!replyText.trim()} onClick={submitReply}
                      >
                        답변 등록
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <style>{`.inquiry-unread-row > td { background: #fff7ed !important; }`}</style>
    </div>
  );
};

export default InquiryPage;
